import { observable, action, reaction, makeObservable } from "mobx";
import { ErrorDisplay } from "../../data/ErrorCodes";
import { IDisposer } from "mobx-utils";
import { RemoveFunctions } from "../../utilities/types";

export interface IDialogState {
    open: boolean;
    loading: boolean;
    error?: ErrorDisplay;
    title?: string;
    cancelText?: string;
    leftButton?: IButtonProps;
    primaryButton?: IButtonProps;
    hideCancelButton?: boolean;
    disableLoadingSpinnerShrink?: boolean; // Removes the shrinking/growing effect, which is helpful when heavy calculations on the main rendering thread block SVG CSS animations
    setOpen: (open: boolean) => void;
    setLoading: (loading: boolean) => void;
    setError?: (error?: ErrorDisplay) => void;
    setLeftButton: (leftButton?: IButtonProps) => void;
    setPrimaryButton: (primaryButton?: IButtonProps) => void;
    setHideCancelButton: (hideCancelButton?: boolean) => void;
    setDisableLoadingSpinnerShrink: (disableLoadingSpinnerShrink?: boolean) => void;
}

interface IButtonProps {
    children: React.ReactNode;
    onClick: () => void;
}

export class DialogState implements IDialogState {
    @observable open = false;
    @observable loading = false;
    @observable error?: ErrorDisplay;
    @observable title?: string;
    @observable cancelText?: string;
    @observable leftButton?: IButtonProps;
    @observable primaryButton?: IButtonProps;
    @observable hideCancelButton?: boolean;
    @observable disableLoadingSpinnerShrink?: boolean;

    private openStateChangedReaction: IDisposer;

    constructor(dialogState?: RemoveFunctions<IDialogState>) {
        makeObservable(this);

        if (dialogState) {
            this.open = dialogState.open;
            this.loading = dialogState.loading;
            this.error = dialogState.error;
            this.title = dialogState.title;
            this.cancelText = dialogState.cancelText;
            this.leftButton = dialogState.leftButton;
            this.primaryButton = dialogState.primaryButton;
            this.hideCancelButton = dialogState.hideCancelButton;
            this.disableLoadingSpinnerShrink = dialogState.disableLoadingSpinnerShrink;
        }

        this.openStateChangedReaction = reaction(
            () => [this.open],
            () => {
                if (this.open) {
                    this.error = undefined;
                }
            }
        );
    }

    @action setOpen(open: boolean) {
        this.open = open;
    }

    @action setLoading(loading: boolean) {
        this.loading = loading;
    }

    @action setError(error?: ErrorDisplay) {
        this.error = error;
    }

    @action setTitle(title: string) {
        this.title = title;
    }

    @action setLeftButton(leftButton?: IButtonProps) {
        this.leftButton = leftButton;
    }

    @action setPrimaryButton(primaryButton?: IButtonProps) {
        this.primaryButton = primaryButton;
    }

    @action setHideCancelButton(hideCancelButton?: boolean) {
        this.hideCancelButton = hideCancelButton;
    }

    @action setDisableLoadingSpinnerShrink(disableLoadingSpinnerShrink?: boolean) {
        this.disableLoadingSpinnerShrink = disableLoadingSpinnerShrink;
    }
}